import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { match } from 'ts-pattern';
import { Toggle } from 'ui-components';
import { Modal, ModalBody, ModalHeaderDefault } from 'ui-components/dist/Modal';
import {
  CopyLinkButtons,
  DownloadPdf,
  Embeds,
} from '~/components/SavedListShareModal';
import { FEATURE_FLAGS, MAX_CREATORS_FOR_LIST_PDF } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { selectTalentManager } from '~/selectors/talent-manager';

import { Feature } from '../Feature';
import { ExportConfirmationModal } from './ExportConfirmationModal';
import { ExportToCSV } from './ExportToCSV';
import { ExportToPDF } from './ExportToPDF';

export const enum SHARE_TYPE {
  embed = 'embed',
  link = 'link',
}

export interface ISavedListShareModalProps {
  isOpen: boolean;
  listId?: string;
  onClose: () => void;
  onGenerateLink?: (enabledControls: string[]) => string | Promise<string>;
  onExport?: () => void;
  shareLink?: string;
  isAudienceFiltersHidden?: boolean;
  exportToEmail?: boolean;
  agencyName?: string;
  agencyLogo?: string;
  listName?: string;
  totalCreators?: number;
}

export const SavedListShareModal: React.FC<ISavedListShareModalProps> = ({
  isOpen,
  listId,
  onClose,
  onGenerateLink,
  onExport,
  shareLink = '',
  isAudienceFiltersHidden = true,
  exportToEmail = false,
  agencyName,
  agencyLogo,
  listName,
  totalCreators = 0,
}) => {
  const isFoamSharedActivityFFEnabled = useFeature(
    FEATURE_FLAGS.foamSharedActivity,
  ).isEnabled;

  const isFoamRosterHideFiltersFFEnabled = useFeature(
    FEATURE_FLAGS.foamRosterHideFilters,
  ).isEnabled;

  const isFoamListPdfFFEnabled = useFeature(
    FEATURE_FLAGS.foamListPdf,
  ).isEnabled;
  const isFoamListDownloadCsvFFEnbaled = useFeature(
    FEATURE_FLAGS.foamListDownloadCsv,
  ).isEnabled;

  const manager = useSelector(selectTalentManager);

  const [audienceFiltersHidden, setAudienceFiltersHidden] = useToggle(
    isAudienceFiltersHidden,
  );

  const {
    isOpen: isExportConfirmationModalOpen,
    onOpen: onExportConfirmationModalOpen,
    onClose: onExportConfirmationModalClose,
  } = useDisclosure();

  const [isEmailRequired, setIsEmailRequired] = useToggle(false);

  const shouldDisplayExportToEmailBtn =
    isFoamListDownloadCsvFFEnbaled && exportToEmail;

  const handleExport = useCallback(() => {
    onClose();
    onExportConfirmationModalOpen();
    onExport?.();
  }, [onClose, onExportConfirmationModalOpen, onExport]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalHeaderDefault title="Share" />
        <ModalBody>
          <Flex direction="column">
            <Flex direction="column" gap={4}>
              <CopyLinkButtons
                shareLink={shareLink}
                listId={listId as string}
                audienceFiltersHidden={
                  isFoamRosterHideFiltersFFEnabled && audienceFiltersHidden
                }
                onGenerateLink={onGenerateLink}
                isEmailRequired={isEmailRequired}
              />
            </Flex>

            <Flex direction="column" padding={4} gap={4} marginTop={1}>
              {isFoamSharedActivityFFEnabled && listId && (
                <Toggle
                  position="right"
                  variant="full"
                  checked={isEmailRequired}
                  onChange={setIsEmailRequired}
                  label="Unlock insights by requiring email to view"
                  name="email-required"
                  tooltip="Receive an email when your shared link is viewed"
                  trackId="foam-share-require-email"
                />
              )}
              {match(isFoamRosterHideFiltersFFEnabled)
                .with(true, () => (
                  <Toggle
                    position="right"
                    variant="full"
                    label="Hide Audience Filters on Shared View"
                    name="audience-filters"
                    checked={audienceFiltersHidden}
                    onChange={setAudienceFiltersHidden}
                    tooltip="Audience performance metrics will not be displayed on the shared view."
                    trackId={
                      audienceFiltersHidden
                        ? 'foam-share-audiencefilters-off'
                        : 'foam-share-audiencefilters-on'
                    }
                  />
                ))
                .otherwise(() => null)}
            </Flex>
            {listId && (
              <>
                {isFoamListPdfFFEnabled && shouldDisplayExportToEmailBtn && (
                  <span className="font-500">
                    {isFoamListDownloadCsvFFEnbaled ? 'Export' : 'Download'}
                  </span>
                )}

                <div className="mt-1 flex w-full gap-4">
                  {shouldDisplayExportToEmailBtn && (
                    <ExportToCSV
                      key="csv"
                      listId={listId}
                      fullWidth={!isFoamListPdfFFEnabled}
                      handleSuccess={handleExport}
                    />
                  )}

                  <Feature name={FEATURE_FLAGS.foamListPdf}>
                    {shouldDisplayExportToEmailBtn ? (
                      <ExportToPDF
                        key="pdf"
                        listId={listId}
                        fullWidth={!shouldDisplayExportToEmailBtn}
                        handleSuccess={handleExport}
                        isDisabled={totalCreators > MAX_CREATORS_FOR_LIST_PDF}
                        disabledTooltip={`Export unavailable: this list exceeds our ${MAX_CREATORS_FOR_LIST_PDF} talent limit.`}
                      />
                    ) : (
                      <DownloadPdf
                        key="pdf"
                        fileName={
                          listName
                            ? listName.toLowerCase().replace(/\s+/g, '-')
                            : 'saved-list'
                        }
                        listId={listId}
                        agencyName={agencyName}
                        agencyLogo={agencyLogo}
                        listName={listName}
                        enabled={isOpen}
                        fullWidth={!shouldDisplayExportToEmailBtn}
                        isButtonDisabled={
                          totalCreators > MAX_CREATORS_FOR_LIST_PDF
                        }
                        disabledTooltip={`Export unavailable: this list exceeds our ${MAX_CREATORS_FOR_LIST_PDF} talent limit.`}
                      />
                    )}
                  </Feature>
                </div>
              </>
            )}

            <Box marginTop={4}>
              <Embeds listId={listId as string} onClose={onClose} />
            </Box>
          </Flex>
        </ModalBody>
      </Modal>
      <ExportConfirmationModal
        isOpen={isExportConfirmationModalOpen}
        onClose={onExportConfirmationModalClose}
        email={manager?.email || ''}
      />
    </>
  );
};
